import { useMemo, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { EntityId, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { AvatarNameSelectorDocument } from 'generated/resources/graphql'
import { compact } from 'lodash'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import LabeledInput from 'modules/core/components/LabeledInput'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { resolveEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import { groupByManagers } from './groups/byManagers'
import { groupByTeams } from './groups/byTeams'
import { groupByWorkplaces } from './groups/byWorkplaces'
import {
  mapEmployees,
  mapSelectedEmployees,
  mapToEmployeeFragment,
  subMapToEmployeeFragment,
} from './lib/mappers'
import {
  EmployeeSelectorMultipleNoAccess,
  EmployeeSelectorMultipleUsingAccess,
  EmployeeSelectorSingleNoAccess,
  EmployeeSelectorSingleUsingAccess,
  GroupType,
  Groups,
} from './types'

const isEmployeeSelectorSingleProps = (
  props:
    | EmployeeSelectorMultipleNoAccess
    | EmployeeSelectorSingleNoAccess
    | EmployeeSelectorMultipleUsingAccess
    | EmployeeSelectorSingleUsingAccess
): props is EmployeeSelectorSingleNoAccess | EmployeeSelectorSingleUsingAccess => {
  return !props.multiple
}

export const EmployeeSelector = (
  props: EmployeeSelectorMultipleNoAccess | EmployeeSelectorSingleNoAccess
) => {
  const {
    excludeCurrentEmployee = false,
    excludeIds = [],
    minWidth,
    width,
    includeInactive = false,
    multiple = false,
    hiddenAvatar = false,
    companyId,
    legalEntityIds,
    employeeIds,
    onlyMyReportees = false,
    allowedGroups = [Groups.ALL, Groups.TEAMS, Groups.MANAGERS],
    defaultGroup = Groups.ALL,
    verb,
  } = props
  const { company, employee } = useCurrentSession()
  const [companyIdState] = useState<string>(companyId ?? company.id)
  const [selectedGroup, setSelectedGroup] = useState<GroupType>(
    allowedGroups.includes(defaultGroup) ? defaultGroup : allowedGroups[0] || Groups.ALL
  )
  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])

  // We skip because we only want to fetch when opening the first time the selector (if we are alwaysopen then we fetch from the very beginning)
  const [skip, setSkip] = useState<boolean>(!(props.alwaysOpen || props.defaultOpen))
  const shouldDeferUntilIdsReady =
    Array.isArray(employeeIds) && employeeIds.length === 0 && props.employeeIds !== undefined

  // Determine which fields to include based on allowed groups
  const includeAll = allowedGroups.includes(Groups.ALL)
  const includeTeams = allowedGroups.includes(Groups.TEAMS)
  const includeManagers = allowedGroups.includes(Groups.MANAGERS)
  const includeWorkplaces = allowedGroups.includes(Groups.WORKPLACES)

  // instead of when the component is rendered.
  const { loading, data } = useQuery(AvatarNameSelectorDocument, {
    variables: {
      companyId: companyIdState,
      ids: employeeIds,
      legalEntityIds,
      active: !includeInactive,
      onlyMyReportees,
      includeTeams,
      includeWorkplaces,
      verb,
    },
    skip: skip || shouldDeferUntilIdsReady,
  })

  const onOpen = () => {
    if (skip) setSkip(false)
  }

  const excludedIds = useMemo(
    () => (excludeIds ?? []).concat(excludeCurrentEmployee && employee?.id ? [employee.id] : []),
    [excludeIds, excludeCurrentEmployee, employee?.id]
  )

  const employeeEntities = useMemo(
    () => mapEmployees(data?.employees.employeeNames, excludedIds, props.displayNameStrategy),
    [data?.employees.employeeNames, excludedIds, props.displayNameStrategy]
  )

  const teamsEntities = useMemo(
    () =>
      includeTeams
        ? groupByTeams(data?.employees.employeeNames, excludedIds, expandedElements)
        : [],
    [includeTeams, data?.employees.employeeNames, excludedIds, expandedElements]
  )

  const managersEntities = useMemo(
    () =>
      includeManagers
        ? groupByManagers(
            data?.employees.employeeNames,
            excludedIds,
            expandedElements,
            onlyMyReportees ? employee : undefined,
            props.displayNameStrategy
          )
        : [],
    [
      includeManagers,
      data?.employees.employeeNames,
      excludedIds,
      expandedElements,
      employee,
      onlyMyReportees,
      props.displayNameStrategy,
    ]
  )

  const workplacesEntities = useMemo(
    () =>
      includeWorkplaces
        ? groupByWorkplaces(data?.employees.employeeNames, excludedIds, expandedElements)
        : [],
    [includeWorkplaces, data?.employees.employeeNames, excludedIds, expandedElements]
  )

  const selectedEmployees = useMemo(
    () =>
      props.multiple
        ? mapSelectedEmployees(selectedGroup, props.value, employeeEntities)
        : props.value
          ? [
              {
                id: props.value.id,
                name: resolveEmployeeDisplayName(
                  {
                    fullName: props.value.fullName,
                    lastName: props.value.lastName,
                    preferredName: props.value.preferredName,
                  },
                  props.displayNameStrategy
                ),
                avatar: props.value.access?.avatar ?? undefined,
              },
            ]
          : [],
    [props.multiple, selectedGroup, props.value, employeeEntities, props.displayNameStrategy]
  )

  const selectedEmployeesForInput = useMemo(() => {
    const employees = Array.isArray(props.value) ? props.value : compact([props.value])

    return employees.map((e) => ({
      id: e.id,
      name: resolveEmployeeDisplayName(
        { fullName: e.fullName, lastName: e.lastName, preferredName: e.preferredName },
        props.displayNameStrategy
      ),
      avatar: e.access?.avatar ?? undefined,
    }))
  }, [props.value, props.displayNameStrategy])

  const selectedTeams = useMemo(
    () =>
      props.multiple && includeTeams
        ? mapSelectedEmployees(selectedGroup, props.value, teamsEntities)
        : undefined,
    [props.multiple, includeTeams, selectedGroup, props.value, teamsEntities]
  )
  const selectedManagers = useMemo(
    () =>
      props.multiple && includeManagers
        ? mapSelectedEmployees(selectedGroup, props.value, managersEntities)
        : undefined,
    [props.multiple, includeManagers, selectedGroup, props.value, managersEntities]
  )

  const selectedWorkplaces = useMemo(
    () =>
      props.multiple && includeWorkplaces
        ? mapSelectedEmployees(selectedGroup, props.value, workplacesEntities)
        : undefined,
    [props.multiple, includeWorkplaces, selectedGroup, props.value, workplacesEntities]
  )

  const groupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: employeeEntities,
      teams: teamsEntities,
      managers: managersEntities,
      workplaces: workplacesEntities,
    }),
    [employeeEntities, teamsEntities, managersEntities, workplacesEntities]
  )

  const selectedGroupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: selectedEmployees ?? [],
      teams: selectedTeams ?? [],
      managers: selectedManagers ?? [],
      workplaces: selectedWorkplaces ?? [],
    }),
    [selectedEmployees, selectedTeams, selectedManagers, selectedWorkplaces]
  )

  const findGroupType = (value: string | null): value is GroupType => {
    const groupValues: string[] = Object.values(Groups)

    return groupValues.includes(value ?? '')
  }

  const handleOnSelectMultiple = (selection: EntitySelectEntity[] | null) => {
    if (!isEmployeeSelectorSingleProps(props)) {
      if (selectedGroup === Groups.ALL) {
        props.onSelect(selection ? selection.map(mapToEmployeeFragment) : [])
      } else {
        props.onSelect(
          selection ? flatSelection(selection).map(subMapToEmployeeFragment).filter(Boolean) : []
        )
      }
    }
  }

  const handleOnSelectSingle = (selection: EntitySelectEntity | null) => {
    if (isEmployeeSelectorSingleProps(props)) {
      if (selectedGroup === Groups.ALL) {
        props.onSelect(selection ? mapToEmployeeFragment(selection) : null)
      } else {
        props.onSelect(selection ? subMapToEmployeeFragment(selection.subItems?.[0]) : null)
      }
    }
  }

  const groups = useMemo(() => {
    return [
      ...(includeAll ? [{ label: i18n.t('employee_selector.groups.none'), value: 'all' }] : []),
      ...(includeTeams
        ? [{ label: i18n.t('employee_selector.groups.teams'), value: 'teams' }]
        : []),
      ...(includeManagers
        ? [{ label: i18n.t('employee_selector.groups.managers.title'), value: 'managers' }]
        : []),
      ...(includeWorkplaces
        ? [{ label: i18n.t('employee_selector.groups.workplaces'), value: 'workplaces' }]
        : []),
    ]
  }, [includeAll, includeTeams, includeManagers, includeWorkplaces])

  return (
    <Box
      minWidth={props.minWidth}
      width={props.width ?? props.minWidth ?? (props.children ? 'fit-content' : 'full')}
      position="relative"
    >
      <EntitySelectWrapper
        {...props}
        value={props.value}
        label={i18n.t('employee_selector.select_employees')}
        hideLabel
        hiddenAvatar={hiddenAvatar}
        loading={loading}
        onOpen={onOpen}
        entities={groupMap[selectedGroup] || []}
        selectedGroup={selectedGroup}
        selectedGroupedEntities={selectedGroupMap[selectedGroup]}
        onGroupChange={(group) => {
          const validGroup =
            findGroupType(group) && allowedGroups.includes(group)
              ? group
              : allowedGroups[0] || Groups.ALL
          setSelectedGroup(validGroup)
        }}
        onSelectExpandedElements={setExpandedElements}
        expandedElements={expandedElements}
        searchPlaceholder={i18n.t('employee_selector.search')}
        selectAllLabel={i18n.t('employee_selector.select_all')}
        clearLabel={i18n.t('employee_selector.clear')}
        selectedLabel={i18n.t('employee_selector.selected')}
        notFoundSubtitle={i18n.t('employee_selector.not_found.subtitle')}
        notFoundTitle={i18n.t('employee_selector.not_found.title')}
        placeholder={i18n.t('employee_selector.select_employees')}
        selectedItemsCopy={i18n.t('employee_selector.employees_selected')}
        groups={groups}
        singleSelector={!multiple}
        onSelect={handleOnSelectSingle}
        onSelectMultiple={handleOnSelectMultiple}
        minWidth={minWidth}
        width={width}
      >
        {props.children ? (
          props.children
        ) : (
          <Box flexDirection="row" width="full">
            <LabeledInput
              aria-label="employee-selector"
              readonly={props.disabled}
              error={props.error}
              mandatory={props.mandatory}
              label={props.label}
              value={props.value?.toString() ?? undefined}
            >
              <Box
                paddingX="s16"
                paddingY="s8"
                flexDirection="row"
                height="s40"
                alignItems="center"
                width="full"
                cursor={props.disabled ? 'default' : 'pointer'}
              >
                <Box flexDirection="row" width="full" gap="s6">
                  {!props.hideSearchIcon && (
                    <Icon.Medium icon="search" color={props.disabled ? 'grey600' : 'grey800'} />
                  )}
                  <Box overflowX="hidden" textAlign="left">
                    {multiple ? (
                      <Text.Normal ellipsis>
                        {selectedEmployeesForInput && selectedEmployeesForInput.length > 0
                          ? selectedEmployeesForInput.length === 1
                            ? selectedEmployeesForInput[0]?.name
                            : `${selectedEmployeesForInput.length} ${i18n.t('common.selected')}`
                          : (props.emptyLabel ??
                            i18n.tp('employees.select_employee', { count: 0 }))}
                      </Text.Normal>
                    ) : (
                      <Text.Normal ellipsis>
                        {!!props.value &&
                        selectedEmployeesForInput &&
                        selectedEmployeesForInput[0]?.name
                          ? selectedEmployeesForInput[0]?.name
                          : (props.emptyLabel ??
                            i18n.tp('employees.select_employee', { count: 1 }))}
                      </Text.Normal>
                    )}
                  </Box>
                </Box>
                <Icon.Small icon="caret-down" />
              </Box>
            </LabeledInput>
          </Box>
        )}
      </EntitySelectWrapper>
    </Box>
  )
}
